/**
 * Auth state. Holds the current user + status; tokens themselves live in the
 * token-store (memory + refresh). On boot, `bootstrap()` decides whether a
 * persisted refresh token can restore a session.
 */
import { create } from 'zustand';
import { apiClient } from '@/shared/api/client';
import { tokenStore } from '@/shared/api/token-store';
import { setOnSessionExpired } from '@/shared/api/session';
import type { User } from '@/shared/types';

type Status = 'idle' | 'authenticating' | 'authenticated' | 'unauthenticated';

interface AuthState {
  user: User | null;
  status: Status;
  error: string | null;
  /** Set on a client's first login — they must change their password. */
  mustChangePassword: boolean;
  login: (username: string, password: string, captchaId: string, captchaText: string) => Promise<void>;
  logout: () => Promise<void>;
  bootstrap: () => void;
  clearError: () => void;
  setPasswordChanged: () => void;
}

const USER_KEY = 'iflow-user';

function readPersistedUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

const MUST_CHANGE_KEY = 'iflow-must-change-pw';

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  status: 'idle',
  error: null,
  mustChangePassword: false,

  async login(username, password, captchaId, captchaText) {
    set({ status: 'authenticating', error: null });
    try {
      const res = await apiClient.login(username, password, captchaId, captchaText);
      tokenStore.setAccess(res.token);
      tokenStore.setRefresh(res.refreshToken);
      localStorage.setItem(USER_KEY, JSON.stringify(res.user));
      const mustChange = res.mustChangePassword ?? false;
      if (mustChange) localStorage.setItem(MUST_CHANGE_KEY, '1');
      else localStorage.removeItem(MUST_CHANGE_KEY);
      set({ user: res.user, status: 'authenticated', error: null, mustChangePassword: mustChange });
    } catch (e) {
      set({
        status: 'unauthenticated',
        error: e instanceof Error ? e.message : 'Login failed',
      });
      throw e;
    }
  },

  async logout() {
    await apiClient.logout();
    tokenStore.clear();
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(MUST_CHANGE_KEY);
    set({ user: null, status: 'unauthenticated', error: null, mustChangePassword: false });
  },

  bootstrap() {
    // A persisted refresh token + cached user lets us optimistically restore the
    // session; the first 401 will trigger a real refresh or bounce to login.
    const refresh = tokenStore.getRefresh();
    const user = readPersistedUser();
    if (refresh && user) {
      set({ user, status: 'authenticated', mustChangePassword: localStorage.getItem(MUST_CHANGE_KEY) === '1' });
    } else {
      set({ status: 'unauthenticated' });
    }
  },

  clearError() {
    set({ error: null });
  },

  setPasswordChanged() {
    localStorage.removeItem(MUST_CHANGE_KEY);
    set({ mustChangePassword: false });
  },
}));

// When the API layer hits an unrecoverable 401 (token expired/missing and the
// refresh token is gone or expired — e.g. reopening the app days later), drop
// the optimistically-restored session so ProtectedRoute bounces to /login.
setOnSessionExpired(() => {
  if (useAuthStore.getState().status === 'unauthenticated') return;
  tokenStore.clear();
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(MUST_CHANGE_KEY);
  useAuthStore.setState({ user: null, status: 'unauthenticated', error: null, mustChangePassword: false });
});
